import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CircularProgress from '@mui/material/CircularProgress';
import {FC, MouseEvent, useState} from 'react';
import {RawTariffDto} from '../../../global/api';
import {extractPdfUrl, openPdfUrl} from '../../../shared/utils/pdf-url';
import {MessageModal} from '../../../ui/message-modal';
import {PERIOD_LABELS} from '../constants/periods.constants';
import {useClientTariffsModel} from '../hooks/use-client-tariffs-model.hook';
import {PeriodKey} from '../types/client-tariffs.types';
import {
  formatPrice,
  formatStorage,
  getFeaturesFromDescription,
  getPeriodTitle,
  getPriceByPeriod,
  normalizeTariffTitle,
} from '../utils/formatters';
import {
  CardsRow,
  Heading,
  InfoBar,
  InfoItem,
  PeriodOption,
  PeriodToggle,
  SaveHint,
  Subtitle,
  TariffsRoot,
  TariffsSurface,
  Title,
} from './client-tariffs.styles';
import {PlanCard} from './plan-card';

export const ClientTariffs: FC = () => {
  const [successState, setSuccessState] = useState<{title: string; message: string; pdfUrl?: string} | null>(null);
  const {
    plans,
    period,
    setPeriod,
    selectedPlanTitle,
    setSelectedPlanTitle,
    selectedPeriodCode,
    connectingTariff,
    currentPlanTitleRaw,
    currentPlanTitleNormalized,
    currentPlanPeriod,
    periodDescription,
    saveHint,
    showSaveHint,
    connectPlan,
  } = useClientTariffsModel();

  const handleSuccessConfirm = () => {
    const pdfUrl = successState?.pdfUrl;
    setSuccessState(null);
    openPdfUrl(pdfUrl);
  };

  return (
    <>
      <TariffsRoot>
        <TariffsSurface>
          <Heading>
            <Title>{`Ваш тарифный план - “${currentPlanTitleRaw.toUpperCase()}”`}</Title>
            <Subtitle>{periodDescription}</Subtitle>
            <PeriodToggle
              value={period}
              exclusive
              onChange={(_event: MouseEvent<HTMLElement>, value: PeriodKey | null) => value && setPeriod(value)}
            >
              <PeriodOption value="month">{PERIOD_LABELS.month}</PeriodOption>
              <PeriodOption value="halfYear">{PERIOD_LABELS.halfYear}</PeriodOption>
              <PeriodOption value="year">{PERIOD_LABELS.year}</PeriodOption>
            </PeriodToggle>
            <SaveHint visible={showSaveHint}>{saveHint || ' '}</SaveHint>
          </Heading>

          <CardsRow>
            {plans.map((plan: RawTariffDto) => {
              const isCurrent =
                normalizeTariffTitle(plan.title) === currentPlanTitleNormalized &&
                selectedPeriodCode === currentPlanPeriod;
              const isSelected = plan.title === selectedPlanTitle;
              const price = getPriceByPeriod(period, plan.durations);
              const features = getFeaturesFromDescription((plan as RawTariffDto & {description?: string}).description);

              return (
                <PlanCard
                  key={plan.id ?? plan.title}
                  name={plan.title}
                  storage={formatStorage(plan.diskSpace)}
                  price={formatPrice(price)}
                  priceUnit="месяц"
                  features={features}
                  highlighted={isSelected}
                  popular={plan.popularPricingPlan}
                  actionActive={isSelected && !isCurrent}
                  onSelect={() => setSelectedPlanTitle(plan.title)}
                  actionLabel={isCurrent ? 'Ваш тариф' : 'Подключить'}
                  actionIcon={
                    isCurrent ? (
                      <CheckCircleIcon sx={{color: '#0D99FF', fontSize: 20}} />
                    ) : connectingTariff ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  actionDisabled={isCurrent || connectingTariff}
                  onAction={() =>
                    connectPlan(plan, ({data}) => {
                      setSuccessState({
                        title: 'Создан счет на оплату',
                        message: `Создан счет на оплату для подключения нового тарифа\n«${plan.title}» на срок\n«${getPeriodTitle(period)}»`,
                        pdfUrl: extractPdfUrl(data),
                      });
                    })
                  }
                />
              );
            })}
          </CardsRow>

          <InfoBar>
            <InfoItem>
              <LockIcon sx={{color: '#0D99FF'}} />
              Безопасная оплата
            </InfoItem>
            <InfoItem>
              <AutorenewIcon sx={{color: '#0D99FF', transform: 'rotate(45deg)'}} />
              Можно сменить тариф в любой момент
            </InfoItem>
            <InfoItem>
              <SupportAgentIcon sx={{color: '#0D99FF'}} />
              Поддержка 24/7
            </InfoItem>
          </InfoBar>
        </TariffsSurface>
      </TariffsRoot>
      <MessageModal
        open={!!successState}
        title={successState?.title || ''}
        message={successState?.message || ''}
        onConfirm={handleSuccessConfirm}
      />
    </>
  );
};
